import { useMemo } from 'react'

import { buildSuggestedDatesParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { logNewRelic } from '../../services/newRelic'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferMetaData from '../Offers/useOfferMetaData'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { determineMemberPricing, onlyNonZeroFees } from 'lib/offer/pricing'
import { isBundlePackageWithPrice } from 'lib/offer/offerTypes'
import resolveLuxPlusMemberPrice from 'lib/search/resolveLuxPlusMemberPrice'

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg?: App.Package | App.BundlePackageWithPrice
  rate?: App.OfferAvailableRate
  duration?: number
  filters?: App.OfferListFilters
  useRealSearchPrices?: boolean // use prices directly from search api
  showPerNightPricing?: boolean
}

interface UseSearchPricesResult {
  /** @deprecated - use defaultPricing` instead */
  totalPrice: number
  /** @deprecated - use memberPricing instead */
  totalMemberPrice?: number
  /** @deprecated - use defaultPricing instead */
  totalValueBase: number
  /** @deprecated - use defaultPricing` instead */
  discountPercent: number
  hotelPrice: number
  hotelMemberPrice: number
  showMemberPrice: boolean
  propertyFees: number
  suggestedDates?: App.OfferSuggestedDates
  defaultPricing: App.PricingWithValue
  memberPricing?: App.PricingWithValue
}

export function useDirectSearchPrices({ filters, offerId }: { filters: App.OfferListFilters, offerId: string }): App.OfferListMetadataPricing | undefined {
  const offerMetadata = useOfferMetaData(offerId, filters)
  if (getShouldUseSearchPrices(filters)) return offerMetadata?.pricing
}

function calculatePerNightPrice(price: number, duration: number = 1) {
  return Math.round(price / duration)
}

export function useSearchPrices({ offer, pkg, rate, filters, useRealSearchPrices = false, showPerNightPricing = false }: Props): UseSearchPricesResult {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector((state) => getSuggestedDates(state, flexibleSearchFilterKey, offer.id))
  const isFlexibleSearch = filters?.flexibleMonths || filters?.flexibleNights

  const rateOrPkg = (rate ?? pkg)
  const baseMemberPrice = rateOrPkg?.memberPrice || 0
  const showMemberPrice = luxPlusEnabled && baseMemberPrice > 0
  const rateOrPkgMemberPrice = rate?.memberPriceWithSurcharge ?? pkg?.memberPrice
  const rooms = useMemo(() => filters?.rooms || [], [filters?.rooms])

  const priceMultiplier = rate ? 1 : (rooms.length || 1)
  const propertyFees = (rate?.propertyFees ?? pkg?.propertyFees ?? 0) * priceMultiplier
  const extraGuestSurcharges = useMemo(() => {
    if (!pkg) return 0

    if (isBundlePackageWithPrice(pkg)) {
      return 0
    }
    return getSumExtraGuestsSurcharge(rooms, offer, pkg)
  }, [pkg, rooms, offer])
  const hotelPrice = ((rateOrPkg?.price ?? 0) + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges
  const hotelMemberPrice = showMemberPrice ? ((rateOrPkgMemberPrice ?? 0 + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges) : 0
  const totalPrice = isFlexibleSearch && suggestedDates?.price ? suggestedDates?.price : hotelPrice + propertyFees
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg?.memberValue : pkg?.value
  const totalValueBase = rate?.hotelValue ?? suggestedDates?.value ?? (pkgValueOrMemberValue ?? 0 * (rooms.length || 1) + extraGuestSurcharges)
  const discountPercent = pkg?.discountPercent || getValueOffPercent(totalValueBase, showMemberPrice && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice)
  const surcharge = (rateOrPkg?.surcharge ?? 0) * priceMultiplier
  const taxesAndFees = (rateOrPkg?.taxesAndFees ?? 0) * priceMultiplier

  const totalMemberPrice = useMemo(() => {
    if (showMemberPrice) {
      let memberPrice: number
      if (isFlexibleSearch && suggestedDates?.memberPrice) {
        memberPrice = suggestedDates.memberPrice
      } else {
        memberPrice = hotelMemberPrice + propertyFees
      }
      return memberPrice
    }
  }, [showMemberPrice, isFlexibleSearch, suggestedDates, hotelMemberPrice, propertyFees])

  return useMemo<UseSearchPricesResult>(() => {
    const fees = onlyNonZeroFees([
      { type: 'property', amount: propertyFees },
      { type: 'taxesAndFees', amount: taxesAndFees },
      { type: 'surcharge', amount: surcharge },
      { type: 'extraGuestSurcharge', amount: extraGuestSurcharges },
    ])

    if (useRealSearchPrices) {
      if (!offerMetadata?.pricing) {
        const fallbackDefaultPricing: App.PricingWithValue = {
          price: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
          value: totalValueBase,
          discountPercent: getValueOffPercent(totalValueBase, totalPrice),
          saleUnit: offer.saleUnit,
          fees,
        }
        const fallbackMemberPricing = determineMemberPricing(fallbackDefaultPricing, totalMemberPrice, totalValueBase)

        // If metadata pricing is absent but we already have rate/package pricing, use it.
        if (rate || pkg) {
          logNewRelic(
            'Search metadata pricing missing; using package/rate fallback pricing',
            { offerId: offer.id, hasRate: !!rate, hasPackage: !!pkg, filters },
            'warn',
          )
          return {
            totalPrice: fallbackDefaultPricing.price,
            totalMemberPrice,
            totalValueBase,
            discountPercent,
            hotelPrice,
            hotelMemberPrice,
            showMemberPrice,
            propertyFees,
            suggestedDates,
            defaultPricing: fallbackDefaultPricing,
            memberPricing: fallbackMemberPricing,
          }
        }

        logNewRelic('No pricing found for offer from search', { offerId: offer.id, filters }, 'error')
        return {
          totalPrice: 0,
          totalMemberPrice: 0,
          totalValueBase: 0,
          discountPercent: 0,
          hotelPrice: 0,
          hotelMemberPrice: 0,
          showMemberPrice: false,
          propertyFees: 0,
          suggestedDates,
          defaultPricing: {
            price: 0,
            value: 0,
            discountPercent: 0,
            saleUnit: offer.saleUnit,
            fees: [],
          },
          memberPricing: undefined,
        }
      }

      const fallbackPrice = offer.lowestPricePackage
      const pricing = offerMetadata?.pricing
      const hasExplicitNoMemberPriceSignalFromSearch = pricing.lowestMemberPrice === 0 &&
        (!rate || (rate.memberPrice ?? 0) === 0)
      const resolvedShowMemberPrice = hasExplicitNoMemberPriceSignalFromSearch ? false : showMemberPrice

      const metadataDiscountPercent = (() => {
        const resolvedLowestMemberPrice = resolveLuxPlusMemberPrice({
          metadataLowestMemberPrice: pricing.lowestMemberPrice,
          packagePrice: pkg?.price,
          packageMemberPrice: pkg?.memberPrice,
          referencePrice: pricing.lowestPrice ?? fallbackPrice?.price ?? 0,
          fallbackMemberPricingPrice: fallbackPrice?.memberPricing?.price,
          fallbackMemberPrice: fallbackPrice?.memberPrice,
        })
        if (hasExplicitNoMemberPriceSignalFromSearch) {
          if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
            return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
          }
          return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
        }
        const hasUsableRateMemberPrice = !!rate?.memberPrice && rate.memberPrice > 0
        const preferredLowestMemberPrice = hasUsableRateMemberPrice &&
          (!resolvedLowestMemberPrice || rate.memberPrice < resolvedLowestMemberPrice)
          ? rate.memberPrice
          : resolvedLowestMemberPrice

        if (resolvedShowMemberPrice && canViewLuxPlusBenefits && preferredLowestMemberPrice !== undefined && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, preferredLowestMemberPrice)
        }
        if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
        }
        return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
      })()

      const metadataTotalPrice = pricing.lowestPrice ?? fallbackPrice?.price ?? 0
      const resolvedMemberPrice = resolveLuxPlusMemberPrice({
        metadataLowestMemberPrice: pricing.lowestMemberPrice,
        packagePrice: pkg?.price,
        packageMemberPrice: pkg?.memberPrice,
        referencePrice: metadataTotalPrice,
        fallbackMemberPricingPrice: fallbackPrice?.memberPricing?.price,
        fallbackMemberPrice: fallbackPrice?.memberPrice,
      })
      const metadataTotalMemberPriceRaw = resolvedMemberPrice ?? 0
      const hasUsableRateMemberPrice = !!rate?.memberPrice && rate.memberPrice > 0
      const metadataTotalMemberPrice = hasExplicitNoMemberPriceSignalFromSearch
        ? 0
        : (hasUsableRateMemberPrice &&
          (!metadataTotalMemberPriceRaw || rate.memberPrice < metadataTotalMemberPriceRaw)
            ? rate.memberPrice
            : metadataTotalMemberPriceRaw)
      const metadataTotalValueBase = pricing?.lowestPriceValue ?? fallbackPrice?.value ?? 0

      const defaultPricing: App.PricingWithValue = {
        price: metadataTotalPrice,
        value: metadataTotalValueBase,
        discountPercent: getValueOffPercent(metadataTotalValueBase, metadataTotalPrice),
        saleUnit: offer.saleUnit,
        fees,
      }
      const memberPricingResolved = determineMemberPricing(defaultPricing, metadataTotalMemberPrice, metadataTotalValueBase)

      return {
        totalPrice: showPerNightPricing ? calculatePerNightPrice(metadataTotalPrice, pkg?.duration) : metadataTotalPrice,
        totalMemberPrice: metadataTotalMemberPrice,
        totalValueBase: metadataTotalValueBase,
        discountPercent: metadataDiscountPercent,
        hotelPrice: pricing.lowestPrice ?? fallbackPrice?.price ?? 0,
        hotelMemberPrice: hasExplicitNoMemberPriceSignalFromSearch ? 0 : (resolvedMemberPrice ?? 0),
        showMemberPrice: resolvedShowMemberPrice,
        propertyFees: fallbackPrice?.propertyFees ?? 0,
        suggestedDates,
        defaultPricing,
        memberPricing: hasExplicitNoMemberPriceSignalFromSearch ? undefined : memberPricingResolved,
      }
    }

    const defaultPricing: App.PricingWithValue = {
      price: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      value: totalValueBase,
      discountPercent: getValueOffPercent(totalValueBase, totalPrice),
      saleUnit: offer.saleUnit,
      fees,
    }

    return {
      totalPrice: showPerNightPricing ? calculatePerNightPrice(totalPrice, pkg?.duration) : totalPrice,
      totalMemberPrice,
      totalValueBase,
      discountPercent,
      hotelPrice,
      hotelMemberPrice,
      showMemberPrice,
      propertyFees,
      suggestedDates,
      defaultPricing,
      memberPricing: determineMemberPricing(defaultPricing, totalMemberPrice, totalValueBase),
    }
  }, [propertyFees, taxesAndFees, surcharge, extraGuestSurcharges, useRealSearchPrices, showPerNightPricing, totalPrice, pkg, totalValueBase, offer, totalMemberPrice, discountPercent, hotelPrice, hotelMemberPrice, showMemberPrice, suggestedDates, offerMetadata?.pricing, filters, rate, canViewLuxPlusBenefits])
}
